import { formatBookingCurrency } from '../../utils/bookingFormat';
import { ADD_ON_MIN_QUANTITY, ADD_ON_MAX_QUANTITY } from '../../hooks/usePublicBookingSelection';
import type { PublicBookingAddOn, SelectedAddOn } from '../../types/publicBooking';

interface AddOnPickerProps {
	addOns: PublicBookingAddOn[];
	/** Chosen add-ons in the order they were picked, each with its quantity. */
	selected: SelectedAddOn[];
	onToggle: (id: number) => void;
	onQuantityChange: (id: number, quantity: number) => void;
	/** Services only, before add-ons — the first line of the running total. */
	serviceOnlyTotal: number;
	total: number;
}

/**
 * The optional second half of the Service step: extras the customer can bolt on.
 *
 * Selecting an add-on puts one of it in the basket and reveals a stepper for
 * anything from 1 to 10 — the ceiling the create API enforces per add-on. The
 * card is a div wrapping a toggle button rather than one big button, because a
 * button cannot legally contain the stepper's own buttons: nesting them makes
 * the inner clicks ambiguous and the markup invalid.
 */
const AddOnPicker = ({
	addOns,
	selected,
	onToggle,
	onQuantityChange,
	serviceOnlyTotal,
	total,
}: AddOnPickerProps) => {
	const quantityById = new Map(selected.map(addOn => [addOn.id, addOn.quantity]));

	return (
		<>
			<div className="pb__section-label">
				Select Add-Ons<span className="pb__section-hint">(optional)</span>
			</div>

			{addOns.length === 0 ? (
				<div className="pb__empty-hint">
					No extras are offered for this booking — continue to pick your date and time.
				</div>
			) : (
				<div className="pb-addon__grid">
					{addOns.map(addOn => {
						const quantity = quantityById.get(addOn.id);
						const isSelected = quantity !== undefined;
						const count = quantity ?? ADD_ON_MIN_QUANTITY;
						return (
							<div
								key={addOn.id}
								className={`pb-addon__card${isSelected ? ' is-selected' : ''}`}
							>
								<button
									type="button"
									className="pb-addon__toggle"
									onClick={() => onToggle(addOn.id)}
									aria-pressed={isSelected}
								>
									<span className="pb-addon__check">✓</span>
									<span className="pb-addon__main">
										<span className="pb-addon__top">
											<span className="pb-addon__name">{addOn.name}</span>
											<span className="pb-addon__price">
												{formatBookingCurrency(addOn.price)}
											</span>
										</span>
										{addOn.description && (
											<span className="pb-addon__desc">
												{addOn.description}
											</span>
										)}
										{addOn.durationMinutes ? (
											<span className="pb-addon__duration">
												+{addOn.durationMinutes} min
											</span>
										) : null}
									</span>
								</button>

								{isSelected && (
									<div className="pb-addon__qty">
										<span className="pb-addon__qty-label">Quantity</span>
										<div className="pb-addon__stepper">
											<button
												type="button"
												className="pb-addon__step"
												onClick={() =>
													onQuantityChange(addOn.id, count - 1)
												}
												disabled={count <= ADD_ON_MIN_QUANTITY}
												aria-label={`Decrease ${addOn.name} quantity`}
											>
												−
											</button>
											<span
												className="pb-addon__qty-value"
												aria-live="polite"
												aria-label={`${addOn.name} quantity`}
											>
												{count}
											</span>
											<button
												type="button"
												className="pb-addon__step"
												onClick={() =>
													onQuantityChange(addOn.id, count + 1)
												}
												disabled={count >= ADD_ON_MAX_QUANTITY}
												aria-label={`Increase ${addOn.name} quantity`}
											>
												+
											</button>
										</div>
										<span className="pb-addon__line-total">
											{formatBookingCurrency(addOn.price * count)}
										</span>
									</div>
								)}
							</div>
						);
					})}
				</div>
			)}

			<div className="pb-addon__summary">
				<div className="pb-addon__sum-title">Running total</div>
				<div className="pb-addon__sum-row">
					<span>Services</span>
					<span>{formatBookingCurrency(serviceOnlyTotal)}</span>
				</div>
				{selected.length > 0 ? (
					selected.map(addOn => (
						<div key={addOn.id} className="pb-addon__sum-row">
							<span>
								{addOn.name}
								{addOn.quantity > 1 && (
									<span className="pb-addon__sum-qty"> × {addOn.quantity}</span>
								)}
							</span>
							<span>{formatBookingCurrency(addOn.lineTotal)}</span>
						</div>
					))
				) : (
					<div className="pb-addon__empty">
						No add-ons selected. You can skip this and continue.
					</div>
				)}
				<div className="pb-addon__sum-total">
					<span>Total</span>
					<span>{formatBookingCurrency(total)}</span>
				</div>
			</div>
		</>
	);
};

export default AddOnPicker;
